import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ShieldCheck, UserPlus, Trash2, RefreshCw } from 'lucide-react';

export const AdminPrayerModerators = () => {
  const { API } = useContext(AuthContext);
  const [mods, setMods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);

  const fetchMods = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/prayer-moderators`);
      setMods(res.data.moderators || []);
    } catch (e) {
      toast.error('Failed to load prayer moderators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMods();
  }, []);

  const setModerator = async (email, enabled) => {
    try {
      const res = await axios.post(`${API}/admin/users/by-email/prayer-moderator`, {
        email,
        enabled,
      });
      return res.data;
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Request failed';
      throw new Error(detail);
    }
  };

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email) {
      toast.error('Enter an email');
      return;
    }
    setAdding(true);
    try {
      const data = await setModerator(email, true);
      toast.success(`Granted prayer moderator: ${data.user?.email || email}`);
      setNewEmail('');
      fetchMods();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove prayer moderator access for ${email}?`)) return;
    setRemovingEmail(email);
    try {
      await setModerator(email, false);
      toast.success(`Removed prayer moderator: ${email}`);
      fetchMods();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRemovingEmail(null);
    }
  };

  return (
    <Card data-testid="prayer-moderators-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
              <ShieldCheck className="w-5 h-5" />
              Manage Prayer Moderators
            </CardTitle>
            <CardDescription>
              Non-admin users who can approve/reject prayer submissions. They only see the Prayer Approval Queue.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMods}
            data-testid="prayer-mods-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add form */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="user@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            disabled={adding}
            className="flex-1"
            data-testid="prayer-mod-email-input"
          />
          <Button
            onClick={handleAdd}
            disabled={adding || !newEmail.trim()}
            className="bg-[#2B6B7F] hover:bg-[#245968] text-white"
            data-testid="prayer-mod-add-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {adding ? 'Adding…' : 'Grant Access'}
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : mods.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="prayer-mods-empty">
            No prayer moderators yet. Add one above.
          </p>
        ) : (
          <div className="space-y-2" data-testid="prayer-mods-list">
            {mods.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50"
                data-testid={`prayer-mod-row-${m.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-800 truncate">{m.name || m.email}</span>
                    {m.role === 'admin' && (
                      <Badge variant="secondary" className="text-xs">Full Admin</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemove(m.email)}
                  disabled={removingEmail === m.email || m.role === 'admin'}
                  title={m.role === 'admin' ? 'Full admins already have full access' : 'Remove prayer moderator access'}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  data-testid={`prayer-mod-remove-${m.email}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {removingEmail === m.email ? 'Removing…' : 'Remove'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPrayerModerators;
